import { Button, Card, Container, Eyebrow, GoldDivider } from '@tt/design-system';
import { QRCodeSVG } from 'qrcode.react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CertificateCard } from '../../components/CertificateCard';
import type { CertificateCardData } from '../../components/CertificateCard';
import { ShareLinks } from '../../components/ShareLinks';
import { mockRegistrations } from '../../data/mockRegistrations';
import { APP_URL, BLOCKCHAIN_PROOF_URL } from '../../lib/env';
import { useAuthStore } from '../../store/authStore';
import { useRegistrationsStore } from '../../store/registrationsStore';

function maskHash(hash: string, head = 8, tail = 8): string {
  if (hash.length <= head + tail + 1) return hash;
  return `${hash.slice(0, head)}…${hash.slice(-tail)}`;
}

export default function Certificate() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const localRegistration = useRegistrationsStore((s) => (id ? s.getById(id) : undefined));
  const mockRegistration = useMemo(
    () => (id ? mockRegistrations.find((r) => r.id === id) : undefined),
    [id],
  );

  const registration = localRegistration ?? mockRegistration;
  const [showVerify, setShowVerify] = useState(false);

  if (!registration) {
    return (
      <Container size="narrow" className="py-24 text-center">
        <Eyebrow tone="gold" className="mb-3">
          Registro não encontrado
        </Eyebrow>
        <h1 className="font-display text-3xl text-parchment md:text-4xl">
          Esse selo não pertence à taça.
        </h1>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-neutral-100">
          Talvez o código esteja errado, ou o registro tenha sido removido.
        </p>
        <Link
          to="/gallery"
          className="mt-8 inline-flex items-center justify-center rounded-md border border-gold/60 px-6 py-3 font-display text-base text-gold transition-colors duration-base hover:bg-gold/10"
        >
          Ver a galeria
        </Link>
      </Container>
    );
  }

  const publicUrl = `${APP_URL.replace(/\/+$/, '')}/c/${registration.id}`;
  const cardData: CertificateCardData = {
    id: registration.id,
    displayName: registration.displayName,
    message: registration.message,
    photoUrl: registration.photoUrl,
    verificationCode: registration.verificationCode,
    createdAt: registration.createdAt,
  };

  const localReg = registration as { verificationHashFake?: string; txHashFake?: string };
  const hashFake: string =
    localReg.verificationHashFake ??
    `Qm${registration.id.replace(/[^a-z0-9]/gi, '').padEnd(44, 'X')}`.slice(0, 46);
  const txFake: string =
    localReg.txHashFake ??
    `0x${registration.id.replace(/[^a-z0-9]/gi, '').padEnd(64, '0').slice(0, 64)}`;

  return (
    <Container size="wide" className="py-16 md:py-24">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
        <div className="flex flex-col items-center">
          <Eyebrow tone="gold" className="mb-4">
            Selo de presença · Copa Feminina FIFA 2027
          </Eyebrow>
          <CertificateCard registration={cardData} variant="public" size="lg" />
        </div>

        <div className="flex flex-col gap-8">
          <Card tone="raised" padding="lg">
            <Eyebrow tone="gold" className="mb-3">
              Autenticidade
            </Eyebrow>
            <h2 className="mb-2 font-display text-2xl text-parchment md:text-3xl">
              Este registro é único.
            </h2>
            <p className="text-sm leading-relaxed text-neutral-100">
              Cada presença é protegida por tecnologia que impede a falsificação. Você pode
              verificar agora mesmo abaixo.
            </p>

            <GoldDivider variant="line" className="my-6" />

            <div className="flex items-start gap-4">
              <div className="rounded-md border border-gold/30 bg-parchment p-2">
                <QRCodeSVG
                  value={publicUrl}
                  size={96}
                  level="M"
                  fgColor="#0B0E1A"
                  bgColor="#F5EFE0"
                  marginSize={0}
                />
              </div>
              <div className="flex flex-col gap-1">
                <p className="font-mono text-[10px] uppercase tracking-stamp text-champagne">
                  Página pública
                </p>
                <p className="break-all font-mono text-xs text-parchment">{publicUrl}</p>
                <p className="mt-2 text-xs italic text-champagne/70">
                  Aponte a câmera do seu celular para o QR Code para abrir esta página.
                </p>
              </div>
            </div>

            <GoldDivider variant="line" className="my-6" />

            <Button
              type="button"
              variant="secondary"
              size="md"
              fullWidth
              onClick={() => setShowVerify((v) => !v)}
              aria-expanded={showVerify}
            >
              {showVerify ? 'Esconder detalhes técnicos' : 'Verificar autenticidade'}
            </Button>

            <div className="mt-3 flex justify-center">
              <a
                href={BLOCKCHAIN_PROOF_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-stamp text-champagne/70 transition-colors duration-fast hover:text-gold"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M10 14L21 3" />
                  <path d="M21 9V3h-6" />
                  <path d="M21 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h5" />
                </svg>
                Ver comprovação na blockchain
              </a>
            </div>

            {showVerify ? (
              <dl className="mt-6 flex flex-col gap-4 text-sm">
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-stamp text-champagne">
                    Código de verificação
                  </dt>
                  <dd className="font-mono text-sm text-parchment">
                    {registration.verificationCode}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-stamp text-champagne">
                    Memória digital permanente
                  </dt>
                  <dd className="break-all font-mono text-xs text-parchment" title={hashFake}>
                    {maskHash(hashFake, 6, 6)}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-stamp text-champagne">
                    Transação na rede de proteção
                  </dt>
                  <dd className="flex flex-col gap-1">
                    <span className="break-all font-mono text-xs text-parchment" title={txFake}>
                      {maskHash(txFake, 10, 8)}
                    </span>
                    <a
                      href={BLOCKCHAIN_PROOF_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="font-mono text-[10px] uppercase tracking-stamp text-gold underline-offset-2 hover:underline"
                    >
                      Ver na blockchain →
                    </a>
                  </dd>
                </div>
                <p className="text-xs italic leading-relaxed text-champagne/60">
                  Esta página exibe os dados públicos do registro. E-mail e identidade da conta
                  permanecem privados.
                </p>
              </dl>
            ) : null}
          </Card>

          <div className="flex flex-col gap-4">
            <Eyebrow tone="champagne">Compartilhe esta presença</Eyebrow>
            <ShareLinks
              url={publicUrl}
              title={`${registration.displayName} está na taça da Copa Feminina 2027`}
              text={`Veja a presença de ${registration.displayName} na taça:`}
            />
          </div>

          <Card tone="subtle" padding="lg" className="text-center">
            <p className="font-display text-lg text-parchment">
              Você também pode estar na taça.
            </p>
            <Link
              to={user ? '/register' : '/login'}
              className="mt-4 inline-flex items-center justify-center rounded-md bg-gold-gradient px-6 py-3 font-display text-base font-semibold text-night shadow-goldMedium transition-shadow duration-base hover:shadow-goldStrong"
            >
              Quero estar na taça
            </Link>
          </Card>
        </div>
      </div>
    </Container>
  );
}
